import {Node} from "@xyflow/react";
import {getNodeDimensions} from "./FamilyTreeNodeSizing";

export const PANNING_PADDING_PX = 50;
export const EDGE_OVERSHOOT_PX = 8;
export const GRAPH_TOP_PADDING_PX = 16;

type NodeBounds = {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    width: number;
    height: number;
    signature: string;
};

export type ViewportLike = {
    x: number;
    y: number;
    zoom: number;
};

export function getPadding(width: number, height: number): number {
    const anchors = [
        {size: 100, padding: 2},
        {size: 320, padding: 0.4},
        {size: 700, padding: 0.4},
        {size: 745, padding: 0.1},
    ];
    const fallbackPadding = 0.1;
    const widthWeight = 0.35;
    const heightWeight = 0.65;
    const size = width * widthWeight + height * heightWeight;

    if (!Number.isFinite(size) || size <= 0) return fallbackPadding;
    if (size <= anchors[0].size) {
        return anchors[0].padding;
    }

    for (let index = 1; index < anchors.length; index += 1) {
        const previous = anchors[index - 1];
        const current = anchors[index];
        if (size <= current.size) {
            const ratio = (size - previous.size) / (current.size - previous.size);
            return previous.padding + ratio * (current.padding - previous.padding);
        }
    }

    return fallbackPadding;
}

export function getNodeBounds(nodes: Node[]): NodeBounds | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let hasPosition = false;

    nodes.forEach(node => {
        if (!node.position) return;
        if (!Number.isFinite(node.position.x) || !Number.isFinite(node.position.y)) return;
        hasPosition = true;
        const {width, height} = getNodeDimensions(node);
        const halfWidth = width / 2;
        const halfHeight = height / 2;

        minX = Math.min(minX, node.position.x - halfWidth);
        minY = Math.min(minY, node.position.y - halfHeight);
        maxX = Math.max(maxX, node.position.x + halfWidth);
        maxY = Math.max(maxY, node.position.y + halfHeight);
    });

    if (!hasPosition || !Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
        return null;
    }

    const width = Math.max(0, maxX - minX);
    const height = Math.max(0, maxY - minY);
    const signature = `${minX}:${minY}:${maxX}:${maxY}:${nodes.length}`;
    return {
        minX,
        minY,
        maxX,
        maxY,
        width,
        height,
        signature
    };
}

function clamp(value: number, min: number, max: number): number {
    const lower = Math.min(min, max);
    const upper = Math.max(min, max);
    return Math.min(Math.max(value, lower), upper);
}

export function clampViewportToCanvas(
    viewport: ViewportLike,
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
    canvasWidthPx: number,
    canvasHeightPx: number,
    extraBottomPaddingPx: number
): ViewportLike {
    const safeZoom = viewport.zoom && viewport.zoom > 0 ? viewport.zoom : 1;
    const safeCanvasWidth = canvasWidthPx && canvasWidthPx > 0 ? canvasWidthPx : 0;
    const safeCanvasHeight = canvasHeightPx && canvasHeightPx > 0 ? canvasHeightPx : 0;
    const bottomOffset = 20;

    if (safeCanvasWidth === 0 || safeCanvasHeight === 0) return viewport;

    const paddingPx = PANNING_PADDING_PX + EDGE_OVERSHOOT_PX;
    const innerLeftPx = paddingPx;
    const innerTopPx = GRAPH_TOP_PADDING_PX;
    const innerRightPx = safeCanvasWidth - paddingPx;
    const innerBottomPx = safeCanvasHeight - (paddingPx - bottomOffset) - extraBottomPaddingPx;

    const leftBoundX = innerLeftPx - bounds.minX * safeZoom;
    const rightBoundX = innerRightPx - bounds.maxX * safeZoom;
    const topBoundY = innerTopPx - bounds.minY * safeZoom;
    const bottomBoundY = innerBottomPx - bounds.maxY * safeZoom;

    const minAllowedX = Math.min(leftBoundX, rightBoundX);
    const maxAllowedX = Math.max(leftBoundX, rightBoundX);
    const minAllowedY = Math.min(topBoundY, bottomBoundY);
    const maxAllowedY = Math.max(topBoundY, bottomBoundY);

    return {
        x: clamp(viewport.x, minAllowedX, maxAllowedX),
        y: clamp(viewport.y, minAllowedY, maxAllowedY),
        zoom: viewport.zoom,
    };
}
